// src/pdf-calcs/pdf-calcs-utils.js
// Shared utility functions for all PDF calculator families (ATM, D-Rating, HLTT).

/**
 * Parse a raw string or number input into a numeric value.
 * Returns NaN for empty, null, or non-numeric input.
 *
 * @param {string|number|null|undefined} raw
 * @returns {number}
 */
export function readNumeric(raw) {
   if (raw === '' || raw == null) return NaN;
   const val = parseFloat(raw);
   return Number.isNaN(val) ? NaN : val;
}

/**
 * Normalise a value according to a field rule.
 *
 * Clamping behaviour is controlled per-rule:
 *   - clampMin (default true): floor the value at rule.min
 *   - clampMax (default false): do NOT ceil the value at rule.max unless explicitly true
 *
 * Non-finite values (NaN, ±Infinity) are returned unchanged so that callers
 * can decide how to handle missing input.
 *
 * Rule shape:
 * {
 *   min: 0,
 *   precision: 3,
 *   warningMax: 500,   // for future UI warnings — not enforced here
 *   clampMin: true,    // optional, defaults to true
 *   clampMax: false,   // optional, defaults to false
 * }
 *
 * @param {number} value
 * @param {object} rule
 * @returns {number}
 */
export function normalizeValue(value, rule) {
   if (!Number.isFinite(value)) return value;

   let normalized = value;

   if (rule.clampMin !== false && Number.isFinite(rule.min)) {
      normalized = Math.max(normalized, rule.min);
   }

   if (rule.clampMax === true && Number.isFinite(rule.max)) {
      normalized = Math.min(normalized, rule.max);
   }

   return normalized;
}

/**
 * Format a normalised value for display in an input field.
 * Rounds to rule.precision decimal places, strips trailing zeros.
 *
 * Returns '' for non-finite values so the field is cleared rather than
 * showing 'NaN' or 'Infinity'.
 *
 * @param {number} value
 * @param {object} rule
 * @returns {string}
 */
export function formatFieldValue(value, rule) {
   if (!Number.isFinite(value)) return '';
   return parseFloat(value.toFixed(rule.precision)).toString();
}

/**
 * Format a calculation result for display (up to 3 decimal places, no trailing zeros).
 *
 * @param {number} val
 * @returns {string}
 */
export function formatResult(val) {
   return parseFloat(val.toFixed(3)).toString();
}

/**
 * Sum an arbitrary number of axle group masses.
 * Returns NaN if any value is non-finite (blank or invalid field), preventing
 * a silently incorrect result being computed from incomplete inputs.
 *
 * @param {...number} values
 * @returns {number}
 */
export function sumFiniteAxles(...values) {
   if (values.some((v) => !Number.isFinite(v))) return NaN;
   return values.reduce((acc, v) => acc + v, 0);
}
